import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, IconButton, Menu, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography, alpha,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Close, DeleteOutline, ErrorOutline, MoreVert } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { AdmissionCycle } from '../../../../types/admissions';
import { admissionCycleService } from '../../../../services/admissions/admissionCycleService';
import useAutoFetch from '../../../../hooks/useAutoFetch';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import DynamicFieldRenderer from '../../form/DynamicFieldRenderer';
import { activeFormSections } from '../../form/admissionFormFields';
import { toSections, useFormDraft } from './useFormDraft';
import SectionRail from './SectionRail';
import FieldCard from './FieldCard';
import AddQuestionMenu from './AddQuestionMenu';

interface Props {
  cycle: AdmissionCycle;
  /** Fields applicants have answered (open cycles): they stay, with their type. */
  answeredKeys?: string[];
  onCancel: () => void;
  onSaved: (next: AdmissionCycle) => void;
}

const NO_PREVIEW_VALUES = {};

/**
 * The cycle's application form, edited in place: sections on the left, the
 * chosen section's questions on the right, and a Preview of exactly what
 * families see. Changes apply to this cycle only.
 */
const FormEditorDialog: React.FC<Props> = ({ cycle, answeredKeys, onCancel, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const answered = useMemo(() => new Set(answeredKeys ?? []), [answeredKeys]);
  const draft = useFormDraft(cycle.formTemplate?.sections ?? [], answered);
  const { sections, ops } = draft;
  const library = useAutoFetch('admission-built-in-fields', admissionCycleService.builtInFields);

  const [selected, setSelected] = useState<string | null>(() => sections[0]?.uid ?? null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [sectionMenu, setSectionMenu] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState<'discard' | 'removeSection' | null>(null);
  const [saving, setSaving] = useState(false);

  const section = sections.find(s => s.uid === selected) ?? sections[0] ?? null;
  const index = section ? sections.indexOf(section) : -1;
  const sectionStays = !!section?.fields.some(draft.mustStay);
  const builtIns = useMemo(() => draft.missingBuiltIns(library.data ?? []), [draft, library.data]);
  const preview = useMemo(() => (mode === 'preview' ? activeFormSections(toSections(sections)) : []), [mode, sections]);

  const sectionId = section?.uid ?? '';
  const editField = useCallback((fieldId: string, patch: Parameters<typeof ops.editField>[2]) => ops.editField(sectionId, fieldId, patch), [ops, sectionId]);
  const moveField = useCallback((fieldId: string, by: -1 | 1) => ops.moveField(sectionId, fieldId, by), [ops, sectionId]);
  const removeField = useCallback((fieldId: string) => ops.removeField(sectionId, fieldId), [ops, sectionId]);

  const close = () => (draft.dirty ? setConfirm('discard') : onCancel());

  const removeSection = () => {
    if (!section) return;
    setConfirm(null);
    ops.removeSection(section.uid);
    setSelected(sections[index === 0 ? 1 : index - 1]?.uid ?? null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await admissionCycleService.update(cycle._id, { formTemplate: { sections: toSections(sections) } });
      enqueueSnackbar('Application form saved', { variant: 'success' });
      onSaved(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t save the form', { variant: 'error' });
      setSaving(false);
    }
  };

  const { stats } = draft;

  return (
    <Dialog open onClose={close} fullWidth maxWidth="lg" PaperProps={{ sx: { height: 'min(880px, 92vh)', borderRadius: '16px', overflow: 'hidden' } }}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>Application form</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            What families fill in to apply to {cycle.name}. Changes apply to this cycle only.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}
          sx={{ '& .MuiToggleButton-root': { px: 2, textTransform: 'none', fontWeight: 600 } }}
        >
          <ToggleButton value="edit">Edit</ToggleButton>
          <ToggleButton value="preview">Preview</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={close} disabled={saving} aria-label="Close"><Close fontSize="small" /></IconButton>
      </Stack>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      {mode === 'preview' ? (
        <Box sx={(t) => ({ flex: 1, overflow: 'auto', bgcolor: alpha(t.palette.text.primary, 0.025), py: 4, px: 2 })}>
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2, textAlign: 'center' }}>
              Exactly what families see, before they fill anything in.
            </Typography>
            <DynamicFieldRenderer sections={preview} values={NO_PREVIEW_VALUES} onChange={() => undefined} cardSections disabled />
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <SectionRail
            sections={sections}
            selected={section?.uid ?? null}
            onSelect={setSelected}
            onAdd={() => setSelected(ops.addSection())}
          />
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            {section ? (
              <Box sx={{ maxWidth: 880, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <TextField
                    value={section.title}
                    onChange={e => ops.editSection(section.uid, { title: e.target.value })}
                    placeholder="Section title"
                    variant="standard"
                    fullWidth
                    InputProps={{ disableUnderline: true, sx: { fontSize: 22, fontWeight: 700 } }}
                    inputProps={{ 'aria-label': 'Section title' }}
                  />
                  <IconButton onClick={e => setSectionMenu(e.currentTarget)} aria-label="Section actions"><MoreVert fontSize="small" /></IconButton>
                  <Menu anchorEl={sectionMenu} open={!!sectionMenu} onClose={() => setSectionMenu(null)}>
                    <MenuItem disabled={index === 0} onClick={() => { setSectionMenu(null); ops.moveSection(section.uid, -1); }}>
                      <ArrowUpward fontSize="small" sx={{ mr: 1.5 }} /> Move up
                    </MenuItem>
                    <MenuItem disabled={index === sections.length - 1} onClick={() => { setSectionMenu(null); ops.moveSection(section.uid, 1); }}>
                      <ArrowDownward fontSize="small" sx={{ mr: 1.5 }} /> Move down
                    </MenuItem>
                    <Tooltip title={sectionStays ? 'It holds questions that have to stay on the form.' : ''} placement="left">
                      <span>
                        <MenuItem
                          disabled={sectionStays}
                          onClick={() => { setSectionMenu(null); if (section.fields.length) setConfirm('removeSection'); else removeSection(); }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteOutline fontSize="small" sx={{ mr: 1.5 }} /> Remove section
                        </MenuItem>
                      </span>
                    </Tooltip>
                  </Menu>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  {section.fields.length ? `${section.fields.length} question${section.fields.length === 1 ? '' : 's'}` : 'No questions yet'}
                </Typography>

                <Stack spacing={1.25}>
                  {section.fields.map((f, i) => (
                    <FieldCard
                      key={f.uid}
                      field={f}
                      answered={answered.has(f.key)}
                      first={i === 0}
                      last={i === section.fields.length - 1}
                      autoFocus={f.uid === justAdded}
                      onChange={editField}
                      onMove={moveField}
                      onRemove={removeField}
                    />
                  ))}
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <AddQuestionMenu
                    builtIns={builtIns}
                    onQuestion={() => setJustAdded(ops.addQuestion(section.uid))}
                    onBuiltIn={(field) => ops.addBuiltIn(section.uid, field)}
                  />
                </Box>
              </Box>
            ) : (
              <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: '100%', p: 4 }}>
                <Typography variant="body1" fontWeight={600}>No sections</Typography>
                <Button variant="outlined" onClick={() => setSelected(ops.addSection())} sx={{ textTransform: 'none' }}>Add a section</Button>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, py: 1.75, borderTop: 1, borderColor: 'divider' }}>
        {draft.problem ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 'auto', color: 'error.main', minWidth: 0 }}>
            <ErrorOutline sx={{ fontSize: 18 }} />
            <Typography variant="body2" noWrap>{draft.problem}</Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {stats.sections} section{stats.sections === 1 ? '' : 's'} · {stats.questions} question{stats.questions === 1 ? '' : 's'} · {stats.required} required
          </Typography>
        )}
        <Button onClick={close} color="inherit" disabled={saving}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={!draft.dirty || !!draft.problem || saving}>
          {saving ? 'Saving…' : 'Save form'}
        </Button>
      </DialogActions>

      <ConfirmationDialog
        open={confirm === 'discard'}
        onClose={() => setConfirm(null)}
        onConfirm={onCancel}
        title="Discard your changes?"
        message="The form stays as it was when you opened it."
        confirmText="Discard"
      />
      <ConfirmationDialog
        open={confirm === 'removeSection'}
        onClose={() => setConfirm(null)}
        onConfirm={removeSection}
        title={`Remove “${section?.title || 'this section'}”?`}
        message={`Its ${section?.fields.length ?? 0} question${section?.fields.length === 1 ? '' : 's'} come off the form too. Built-in ones can be added back from Add question.`}
        confirmText="Remove section"
      />
    </Dialog>
  );
};

export default FormEditorDialog;
